// PostHog trace of the Alerts (activity) pane: the list seed, the pane opening, a mark-read and a
// badge that outruns the rows the workspace holds ("badge 2, empty list"). Counted outcomes through
// client-log, typed by the one catalog (@cyborg7/observability/ui-kpi): counts only, never a row's text.
import type { UiKpiFields, UiKpiSource } from "@cyborg7/observability/ui-kpi";
import { reportKpi, shouldReport } from "$lib/report-failure.js";

export type AlertsCounts = UiKpiFields<"alerts.badge_mismatch">;
export interface AlertsSnapshot {
  counts: AlertsCounts;
  hasMore: boolean;
}

interface AlertsCountSource {
  badgeCount: number;
  unreadCount: number;
  hasMore: boolean;
  items: ReadonlyArray<{ isRead: boolean; foldedServerIds?: readonly string[] }>;
  serverTotalUnread: number | undefined;
}

export const MISMATCH_SETTLE_MS = 3_000;
const MISMATCH_THROTTLE_MS = 10 * 60_000;
const THROTTLE_MS = 1_000;

// One card can carry folded server rows: it holds 1 + foldedServerIds.length unread rows.
export function cardRows(item: { foldedServerIds?: readonly string[] }): number {
  return 1 + (item.foldedServerIds?.length ?? 0);
}

export function alertsCounts(s: AlertsCountSource): AlertsSnapshot {
  let held = 0;
  for (const i of s.items) if (!i.isRead) held += cardRows(i);
  return {
    counts: {
      badge: s.badgeCount,
      rows_held: held,
      server_ws_unread: s.unreadCount,
      server_total: s.serverTotalUnread,
    },
    hasMore: s.hasMore,
  };
}

// This workspace's unread is above the unread rows held, and no unloaded page explains it (or
// nothing unread is held at all: "badge 2, empty list"). The cross-workspace badge is payload only.
export function isBadgeMismatch({ counts: c, hasMore }: AlertsSnapshot): boolean {
  return c.server_ws_unread > c.rows_held && (!hasMore || c.rows_held === 0);
}

// Fires when the gap holds now AND still after a settle (a live push or a seed in flight closes
// it on its own). `read` returns undefined once the workspace changed.
export function checkBadgeMismatch(
  workspaceId: string,
  read: () => AlertsSnapshot | undefined,
  settleMs = MISMATCH_SETTLE_MS,
): void {
  const first = read();
  if (!first || !isBadgeMismatch(first)) return;
  setTimeout(() => {
    const now = read();
    if (!now || !isBadgeMismatch(now)) return;
    if (shouldReport("alerts.badge_mismatch", MISMATCH_THROTTLE_MS)) {
      reportKpi("alerts.badge_mismatch", { workspaceId, ...now.counts });
    }
  }, settleMs);
}

function once<S extends UiKpiSource>(
  key: string,
  source: S,
  fields: UiKpiFields<S> & { workspaceId: string },
): void {
  if (shouldReport(`${source}:${key}`, THROTTLE_MS)) reportKpi(source, fields);
}

export function reportAlertsSeed(
  workspaceId: string,
  rows: number,
  unread: number,
  total: number | undefined,
  ms: number,
): void {
  once(workspaceId, "alerts.seed", { workspaceId, rows, unread, total, ms: Math.round(ms) });
}

export function reportAlertsOpened(workspaceId: string, unread: number, rows: number): void {
  once(workspaceId, "alerts.opened", { workspaceId, unread, rows });
}

export function reportAlertsMarkRead(
  workspaceId: string,
  count: number,
  scope: "one" | "all",
): void {
  reportKpi("alerts.mark_read", { workspaceId, count, scope });
}

// Invariant beacon: badge units outside this workspace that no door in the pane leads to.
export function reportDoorlessUnread(
  workspaceId: string,
  others: number,
  candidates: number,
  split: boolean,
): void {
  if (shouldReport(`alerts.doorless_unread:${workspaceId}`, MISMATCH_THROTTLE_MS)) {
    reportKpi("alerts.doorless_unread", { workspaceId, others, candidates, split });
  }
}

export function reportOtherWorkspaceOpened(workspaceId: string, count: number): void {
  reportKpi("alerts.other_workspace_opened", { workspaceId, count });
}
